/**
 * Initialise a tabs interface for either `tabs` or `tabs-loop` blocks.
 * Wires up tab clicks, optional mobile dropdown, autoplay arrow nav, and
 * keeps `aria-selected` on the tab link (the `<a>`, not the `<li>`).
 *
 * @param {string} containerSelector CSS selector for the block wrapper.
 * @param {string} panelSelector     CSS selector for tab panel elements (each is identified by `data-id="panel-N"`).
 */
export function initTabs(containerSelector, panelSelector) {
	document.addEventListener('DOMContentLoaded', () => {
		const tabsContainers = document.querySelectorAll(containerSelector);

		tabsContainers.forEach(tabsContainer => {
			const parentUL = tabsContainer.querySelector('ul');
			const tabs = Array.from(tabsContainer.querySelectorAll('.tabs-title a'));
			const navButtons = tabsContainer.querySelector('.tabs-navigation');
			const selectDropdown = tabsContainer.querySelector('.tabs-dropdown-menu-mobile');
			const autoplay = navButtons && navButtons.getAttribute('data-auto-play') === 'true';
			const autoplaySpeed = navButtons ? parseInt(navButtons.getAttribute('data-auto-play-speed'), 10) || 3000 : 3000;
			const prevButton = navButtons ? navButtons.querySelector('.previous-tab') : null;
			const nextButton = navButtons ? navButtons.querySelector('.next-tab') : null;

			let currentTabIndex = tabs.findIndex(tab => tab.parentElement.classList.contains('is-active'));
			let autoplayInterval;

			if (selectDropdown) {
				selectDropdown.value = `#panel-${currentTabIndex}`;
			}

			const activateTab = (index) => {
				const prevActiveContent = tabsContainer.querySelector(`${panelSelector}.is-active`);
				const prevActiveTab = parentUL.querySelector('.tabs-title.is-active');

				if (prevActiveContent) {
					prevActiveContent.classList.remove('is-active');
					prevActiveContent.setAttribute('aria-hidden', 'true');
					prevActiveTab.classList.remove('is-active');
					const prevActiveLink = prevActiveTab.querySelector('a');
					if (prevActiveLink) {
						prevActiveLink.setAttribute('aria-selected', 'false');
					}
				}

				const newActiveContent = tabsContainer.querySelector(`${panelSelector}[data-id="panel-${index}"]`);
				const newActiveTab = tabs[index].parentElement;

				newActiveContent.classList.add('is-active');
				newActiveContent.setAttribute('aria-hidden', 'false');
				newActiveTab.classList.add('is-active');
				tabs[index].setAttribute('aria-selected', 'true');

				currentTabIndex = index;

				if (selectDropdown) {
					selectDropdown.value = `#panel-${index}`;
				}
			};

			parentUL.addEventListener('click', (event) => {
				event.preventDefault();
				const link = event.target.closest('.tabs-title > a');
				if (!link) return;
				const index = tabs.findIndex(tab => tab === link);
				activateTab(index);
			});

			if (tabsContainer.classList.contains('has-dropdown-menu-mobile')) {
				if (selectDropdown) {
					selectDropdown.addEventListener('change', (event) => {
						const targetHref = event.target.value;
						const index = tabs.findIndex(tab => tab.getAttribute('href') === targetHref);
						activateTab(index);
					});
				}
			}

			const switchTab = (direction) => {
				if (direction === 'next') {
					currentTabIndex = (currentTabIndex + 1) % tabs.length;
				} else if (direction === 'prev') {
					currentTabIndex = (currentTabIndex - 1 + tabs.length) % tabs.length;
				}
				activateTab(currentTabIndex);
			};

			const startAutoplay = () => {
				if (autoplay) {
					autoplayInterval = setInterval(() => {
						switchTab('next');
					}, autoplaySpeed);
				}
			};

			const stopAutoplay = () => {
				clearInterval(autoplayInterval);
			};

			if (autoplay && (prevButton || nextButton)) {
				startAutoplay();
				tabsContainer.addEventListener('mouseenter', stopAutoplay);
				tabsContainer.addEventListener('mouseleave', startAutoplay);
			}

			if (prevButton) {
				prevButton.addEventListener('click', () => switchTab('prev'));
			}
			if (nextButton) {
				nextButton.addEventListener('click', () => switchTab('next'));
			}
		});
	});
}
